import { useState, useCallback } from 'react';
import { apiRequest } from '../../utils/apiUtils';

export interface AnalyticsSummary {
  totalRevenue: number;
  totalBookings: number;
  averageOccupancy: number;
  averageRating: number;
  revenueChange: number;
  bookingsChange: number;
  occupancyChange: number;
}

export interface RevenueDataPoint {
  date: string;
  revenue: number;
  bookings: number;
}

export interface OccupancySlot {
  hour: number;
  occupancyRate: number;
  bookings: number;
}

export interface OccupancyDay {
  day: number;
  dayName: string;
  slots: OccupancySlot[];
}

export interface ChannelDataPoint {
  channel: string;
  count: number;
  revenue: number;
}

export const useOwnerAnalytics = () => {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueDataPoint[]>([]);
  const [occupancyData, setOccupancyData] = useState<OccupancyDay[]>([]);
  const [channelData, setChannelData] = useState<ChannelDataPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async (timeRange: string) => {
    setLoading(true);
    setError(null);

    const response = await apiRequest<any>(
      `/owner/analytics/stats?timeRange=${timeRange}`
    );

    if (response.data) {
      // Transform backend data to summary format
      const data = response.data;
      setSummary({
        totalRevenue: data.revenue?.total || 0,
        totalBookings: data.bookings?.total || 0,
        averageOccupancy: data.occupancy?.rate || 0,
        averageRating: data.reviews?.avgRating || 0,
        revenueChange: data.revenue?.percentChange || 0,
        bookingsChange: 0, // Calculate if needed
        occupancyChange: 0, // Calculate if needed
      });
    } else {
      setError(response.error || 'Error al cargar estadísticas');
    }

    setLoading(false);
  }, []);

  const fetchRevenueData = useCallback(async (timeRange: string, groupBy: string) => {
    const response = await apiRequest<RevenueDataPoint[]>(
      `/owner/analytics/revenue?timeRange=${timeRange}&groupBy=${groupBy}`
    );

    if (response.data) {
      setRevenueData(response.data);
    }
  }, []);

  const fetchOccupancyData = useCallback(async (venueId: string, week: string) => {
    const response = await apiRequest<any[]>(
      `/owner/analytics/occupancy/${venueId}?week=${week}`
    );

    if (response.data) {
      // Transform flat array to grouped by day
      const groupedByDay = response.data.reduce((acc: any, slot: any) => {
        const existing = acc.find((d: any) => d.day === slot.day);
        if (existing) {
          existing.slots.push({
            hour: slot.hour,
            occupancyRate: slot.occupancy,
            bookings: slot.bookings,
          });
        } else {
          acc.push({
            day: slot.day,
            dayName: slot.dayName,
            slots: [{
              hour: slot.hour,
              occupancyRate: slot.occupancy,
              bookings: slot.bookings,
            }],
          });
        }
        return acc;
      }, []);

      setOccupancyData(groupedByDay);
    }
  }, []);

  const fetchChannelData = useCallback(async (timeRange: string) => {
    const response = await apiRequest<{ channels: ChannelDataPoint[] }>(
      `/owner/analytics/channels?timeRange=${timeRange}`
    );

    if (response.data?.channels) {
      setChannelData(response.data.channels);
    }
  }, []);

  const exportCSV = useCallback(async (format: string, timeRange: string) => {
    const response = await apiRequest(
      `/owner/analytics/export?format=${format}&timeRange=${timeRange}`,
      { requireAuth: true }
    );

    if (response.data) {
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  }, []);

  return {
    summary,
    revenueData,
    occupancyData,
    channelData,
    loading,
    error,
    fetchSummary,
    fetchRevenueData,
    fetchOccupancyData,
    fetchChannelData,
    exportCSV,
  };
};
